import { useCallback, useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DescriptionIcon from '@mui/icons-material/Description';
import { useDropzone } from 'react-dropzone';

const ACCEPT_MIME: Record<string, string[]> = {
  'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'],
  'application/pdf': ['.pdf'],
  'application/zip': ['.zip'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
};

const DEFAULT_MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB

/** Tamano maximo por archivo (bytes), reutilizable por otros componentes. */
export const MAX_FILE_BYTES = DEFAULT_MAX_FILE_BYTES;

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Valida si un archivo es de un tipo permitido (mismos tipos que el dropzone).
 * Util para validar archivos pegados con Ctrl+V donde no interviene react-dropzone.
 * Acepta por MIME (incluye comodines como image/*) o por extension del nombre.
 */
export function isAcceptedFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return Object.entries(ACCEPT_MIME).some(([mime, exts]) => {
    if (mime.endsWith('/*')) {
      const prefix = mime.slice(0, -1); // "image/*" -> "image/"
      if (type.startsWith(prefix)) return true;
    } else if (type && type === mime) {
      return true;
    }
    return exts.some((ext) => name.endsWith(ext));
  });
}

const iconForType = (file: File) => {
  const t = file.type || '';
  if (t.startsWith('image/')) return <ImageIcon fontSize="small" />;
  if (t.includes('pdf')) return <PictureAsPdfIcon fontSize="small" />;
  if (t.includes('zip')) return <FolderZipIcon fontSize="small" />;
  return <DescriptionIcon fontSize="small" />;
};

export type FileDropzoneFieldProps = {
  /** Lista controlada de archivos. */
  value: File[];
  /** Callback al cambiar el set de archivos (agregar / quitar). */
  onChange: (files: File[]) => void;
  /** Tamano maximo por archivo en bytes (default 50 MB). */
  maxFileBytes?: number;
  /** Texto adicional debajo del label. */
  helperText?: string;
  /** Mensaje de error en rojo (ej. validacion externa). */
  error?: string | null;
  /** Permite multiples archivos. Default true. */
  multiple?: boolean;
};

/**
 * Componente de drag&drop reutilizable. Mantiene el estado de los
 * archivos controlado por el padre — emite onChange con el array
 * completo cada vez que el usuario suelta o quita un archivo.
 *
 * Tipos aceptados: imagenes, PDF, ZIP, Excel, Word, CSV, TXT. Para
 * cambiar la lista, modificar {@code ACCEPT_MIME} en este archivo
 * (deberia mantenerse sincronizado con la lista MIME_PERMITIDOS del
 * backend en {@code TicketArchivoService}).
 */
export default function FileDropzoneField({
  value,
  onChange,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
  helperText,
  error,
  multiple = true,
}: FileDropzoneFieldProps) {
  const [localError, setLocalError] = useState<string | null>(null);

  const onDrop = useCallback((accepted: File[]) => {
    setLocalError(null);
    const aceptados = accepted.filter(f => {
      if (f.size > maxFileBytes) {
        setLocalError(`"${f.name}" excede el limite de ${formatBytes(maxFileBytes)}`);
        return false;
      }
      return true;
    });
    onChange(multiple ? [...value, ...aceptados] : aceptados.slice(0, 1));
  }, [maxFileBytes, multiple, onChange, value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT_MIME,
    maxSize: maxFileBytes,
    multiple,
  });

  const quitar = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const errorText = error ?? localError;

  return (
    <Stack spacing={1}>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          borderRadius: 1,
          p: 1.5,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <input {...getInputProps()} />
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <CloudUploadIcon color={isDragActive ? 'primary' : 'action'} />
          <Typography variant="body2" color="text.secondary">
            {isDragActive
              ? 'Suelte los archivos aquí…'
              : multiple
                ? 'Arrastre archivos aquí o haga clic para seleccionar'
                : 'Arrastre un archivo aquí o haga clic para seleccionar'}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          {helperText
            ?? `Imágenes, PDF, ZIP, Excel, Word, CSV — máx. ${formatBytes(maxFileBytes)} por archivo`}
        </Typography>
      </Box>

      {value.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {value.map((f, idx) => (
            <Chip
              key={`${f.name}-${idx}`}
              icon={iconForType(f)}
              label={`${f.name} (${formatBytes(f.size)})`}
              onDelete={() => quitar(idx)}
              deleteIcon={<CloseIcon />}
              size="small"
              variant="outlined"
              color="primary"
            />
          ))}
        </Stack>
      )}

      {errorText && (
        <Typography variant="caption" color="error">
          {errorText}
        </Typography>
      )}
    </Stack>
  );
}
